import { useCallback, useMemo } from 'react';
import {
  applicationSectionsFor,
  consumerQuoteCarrier,
  consumerQuoteKey,
  consumerQuotePremium,
  isHybridConsumerQuote,
  paymentScheduleOfQuote,
  prefillApplication,
} from '@shared/consumerPlan';
import { useConsumerClient } from './use-consumer-client';
import { useCoverageSelection } from './use-coverage-selection';
import { useSubmitApplication } from './use-consumer-requests';

/**
 * The consumer's application: answers saved on their plan (prefilled from the intake the
 * first time), the sections that apply to the chosen product, and sending it to the team.
 */
export function useApplication(clientId: string) {
  const { client } = useConsumerClient(clientId);
  const { plan, updatePlan, selectedQuote, selectedProjection } = useCoverageSelection(clientId);
  const submit = useSubmitApplication(clientId);

  const answers = useMemo(
    () => plan?.application ?? prefillApplication(client?.intakeSurvey ?? {}),
    [plan?.application, client?.intakeSurvey],
  );
  const paysDeathBenefit =
    !!selectedQuote && isHybridConsumerQuote(selectedQuote) && (selectedQuote.deathBenefitInitial ?? 0) > 0;
  const sections = useMemo(() => applicationSectionsFor(paysDeathBenefit), [paysDeathBenefit]);

  const saveAnswers = useCallback(
    (patch: Partial<ConsumerApplicationAnswers>) => updatePlan({ application: { ...answers, ...patch } }),
    [answers, updatePlan],
  );

  const coverage = useMemo<ConsumerApplicationCoverage | null>(
    () =>
      selectedQuote && selectedProjection
        ? {
            quoteKey: consumerQuoteKey(selectedQuote),
            carrierName: consumerQuoteCarrier(selectedQuote),
            productName: selectedQuote.productName,
            paymentSchedule: paymentScheduleOfQuote(selectedQuote) ?? 'ongoing',
            premium: consumerQuotePremium(selectedQuote),
            projectedPayout: selectedProjection.projectedPayout,
            outOfPocket: selectedProjection.outOfPocket,
          }
        : null,
    [selectedQuote, selectedProjection],
  );

  const send = useCallback(
    (consentIds: string[], onSent: () => void) => {
      if (coverage) submit.mutate({ coverage, answers, consentIds }, { onSuccess: onSent });
    },
    [answers, coverage, submit],
  );

  return {
    answers,
    sections,
    saveAnswers,
    selectedQuote,
    canSend: !!coverage,
    send,
    isSending: submit.isPending,
    sendFailed: submit.isError,
  };
}
